/* eslint-disable harvey/no-straight-quotes */
import { getOoxmlContainingCursor } from './formatting-utils'

/**
 * Extracts the w:body tag from the OOXML.
 * @param fullOoxml The full OOXML content.
 * @returns The body tag of the OOXML.
 */
export const getWordOoxmlBody = (fullOoxml: string): string => {
  const parser = new DOMParser()
  // eslint-disable-next-line office-addins/load-object-before-read
  const xmlDoc = parser.parseFromString(fullOoxml, 'text/xml')
  const bodyElement = xmlDoc.getElementsByTagName('w:body')
  const serializer = new XMLSerializer()
  return serializer.serializeToString(bodyElement[0])
}

/**
 * Pretty prints OOXML strings to the browser console in a structured format
 * https://stackoverflow.com/questions/376373/pretty-printing-xml-with-javascript
 * @param ooxmlString - The OOXML string to format and print
 * @param label - Optional label to display before the formatted OOXML
 */
export const prettyPrintOoxml = (
  ooxmlString: string,
  label: string = 'Pretty OOXML'
): void => {
  // Only log in dev/staging.
  if (process.env.NODE_ENV === 'production') {
    return
  }
  const xmlDoc = new DOMParser().parseFromString(ooxmlString, 'application/xml')
  const xsltDoc = new DOMParser().parseFromString(
    [
      // describes how we want to modify the XML - indent everything
      '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
      '  <xsl:strip-space elements="*"/>',
      '  <xsl:template match="para[content-style][not(text())]">',
      '    <xsl:value-of select="normalize-space(.)"/>',
      '  </xsl:template>',
      '  <xsl:template match="node()|@*">',
      '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
      '  </xsl:template>',
      '  <xsl:output indent="yes"/>',
      '</xsl:stylesheet>',
    ].join('\n'),
    'application/xml'
  )

  const xsltProcessor = new XSLTProcessor()
  xsltProcessor.importStylesheet(xsltDoc)
  const resultDoc = xsltProcessor.transformToDocument(xmlDoc)
  const resultXml = new XMLSerializer().serializeToString(resultDoc)
  // eslint-disable-next-line no-console
  console.groupCollapsed(`%c${label}`, 'color: #4CAF50; font-weight: bold;')
  console.info(resultXml)
  console.groupEnd()
}

export const processOoxmlContent = (xmlString: string): string => {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml')
  const allowed = new Set(['/word/document.xml', '/_rels/.rels'])

  const root = xmlDoc.documentElement.cloneNode(false) as Element

  const parts = Array.from(xmlDoc.getElementsByTagName('pkg:part')).filter(
    (part) => allowed.has(part.getAttribute('pkg:name')!)
  )
  parts.forEach((part) => root.appendChild(part.cloneNode(true)))

  const serializer = new XMLSerializer()
  return serializer.serializeToString(root)
}

export const insertOoxmlAtCursor = async (
  xml: string,
  inParagraph: boolean = false
) => {
  prettyPrintOoxml(getWordOoxmlBody(xml), 'xml to insert')
  const ooxmlContainingCursor = await getOoxmlContainingCursor()
  const initialBody = getWordOoxmlBody(ooxmlContainingCursor)
  prettyPrintOoxml(initialBody, 'ooxml before insert')
  let xmlToInsert = xml

  // If we're starting a new paragraph, the OfficeJS insertOoxml reconciliation will
  // strip certain formatting elements (indentation, etc) unless we prefix with an empty
  // paragraph element at the end.
  if (!inParagraph) {
    try {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlToInsert, 'application/xml')

      const bodyElement = xmlDoc.getElementsByTagName('w:body')
      const emptyParagraph = xmlDoc.createElement('w:p')
      bodyElement[0].appendChild(emptyParagraph)

      const serializer = new XMLSerializer()
      xmlToInsert = serializer.serializeToString(xmlDoc)
    } catch (error) {
      console.error('Error adding empty paragraph to XML:', error)
      return
    }
  }

  return await Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll
    const selection = context.document.getSelection()
    await context.sync()
    selection.insertOoxml(xmlToInsert, Word.InsertLocation.replace)
    await context.sync()
    const ooxmlContainingCursor = await getOoxmlContainingCursor()
    prettyPrintOoxml(
      getWordOoxmlBody(ooxmlContainingCursor),
      'ooxml after insert'
    )
  })
}
